import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { FieldError, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showErrorToast, showSuccessToast } from '@Helpers';

import { sendErrorEvent } from '@Analytics';
import Label from '@Components/ui/label';
import { Button } from '@Components/ui-v2/button';
import { Input } from '@Components/ui-v2/input';
import { CredentialField, CredentialFieldKey, ProviderCredential, StandardCredentialFieldKey } from '@Models';
import { bopsApi } from '@Network';

export interface CredentialsFormProps {
  /** Display name of the connector ("Walmart", "Soriana"). */
  connectorName: string;
  /** Backend connector type identifier ("wrl", "soriana"). */
  connectorType: string;
  /** Field schema for this connector. */
  fields: CredentialField[];
  /** When updating, the current values used to prefill non-secret fields. */
  existingCredential?: ProviderCredential;
  mode: 'create' | 'update';
  onCancel: () => void;
  /** Called after a successful create/update so the page can refresh data. */
  onSuccess: () => void;
  /**
   * Overrides the create submit call — defaults to `bopsApi.postCreateCredentials`.
   * Used by the token-gated pre-onboarding credential intake page, which posts to a
   * separate, unauthenticated endpoint instead of the authenticated platform one.
   */
  submitCreate?: (credential: ProviderCredential) => Promise<void>;
}

type FormFields = Partial<Record<CredentialFieldKey, string>>;

const ERROR_CODES: Record<string, string> = {
  SAME_PASSWORD: 'Credentials.SamePassword',
  SAME_TOKEN: 'Credentials.SameToken',
  INVALID_CREDENTIALS: 'Credentials.ErrorInvalidCredentials',
  INVALID_URL_SCHEME: 'Credentials.InvalidUrlScheme',
  INVALID_URL_HOST: 'Credentials.InvalidUrlHost',
  INVALID_URL_SIGNATURE: 'Credentials.InvalidUrlSignature',
  INVALID_URL_MISSING_ID: 'Credentials.InvalidUrlMissingId',
};

// Only WRL's authenticator tells us *why* a login was rejected today (see
// validate_with_reason on the backend) — every other connector's 403 has no
// status_reason, so falls through to ERROR_CODES.INVALID_CREDENTIALS above.
const STATUS_REASON_CODES: Record<string, string> = {
  RPA_BOT_BLOCKED: 'Credentials.StatusReason.RpaBotBlocked',
  USER_BLOCKED: 'Credentials.StatusReason.UserBlocked',
  INVALID_USER: 'Credentials.StatusReason.InvalidUser',
  INVALID_PASSWORD: 'Credentials.StatusReason.InvalidPassword',
  PASSWORD_EXPIRED: 'Credentials.StatusReason.PasswordExpired',
  PASSWORD_CHANGE_REQUIRED: 'Credentials.StatusReason.PasswordChangeRequired',
  CREDENTIALS_EXPIRED: 'Credentials.StatusReason.CredentialsExpired',
  EMAIL_VERIFICATION_REQUIRED: 'Credentials.StatusReason.EmailVerificationRequired',
};

const isSecretField = (field: CredentialField): boolean => field.isSecret ?? field.type === 'password';

interface FieldInputProps {
  field: CredentialField;
  mode: 'create' | 'update';
  register: UseFormRegister<FormFields>;
  watch: UseFormWatch<FormFields>;
  error?: FieldError;
}

const CredentialFieldInput = ({ field, mode, register, watch, error }: FieldInputProps) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const isPassword = field.type === 'password';
  const isSecret = isSecretField(field);
  const fieldValue = watch(field.storageKey) ?? '';
  const hasTyped = fieldValue.length > 0;
  const effectiveVisible = isVisible && hasTyped;
  const placeholder = mode === 'update' && isSecret ? '••••••••' : t(field.placeholderKey ?? field.labelKey);

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={field.storageKey} className="text-xs">
        {t(field.labelKey)}
      </Label>
      <div className={isPassword ? 'relative flex' : ''}>
        <Input
          type={isPassword && !effectiveVisible ? 'password' : 'text'}
          id={field.storageKey}
          placeholder={placeholder}
          aria-label={t(field.labelKey)}
          autoComplete={isSecret ? 'new-password' : undefined}
          className={isPassword ? 'pr-10' : undefined}
          {...register(field.storageKey, {
            required: !isSecret || mode === 'create' ? `${t(field.labelKey)} ${t('Global.Required')}` : undefined,
          })}
        />
        {isPassword && hasTyped && (
          <button
            type="button"
            onClick={() => setIsVisible((v) => !v)}
            aria-label={effectiveVisible ? t('Credentials.Form.HidePassword') : t('Credentials.Form.ShowPassword')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
            <FontAwesomeIcon icon={effectiveVisible ? faEyeSlash : faEye} />
          </button>
        )}
      </div>
      {error && <p className="text-xs text-error-600">{error.message}</p>}
    </div>
  );
};

const CredentialsForm = ({
  connectorName,
  connectorType,
  fields,
  existingCredential,
  mode,
  onCancel,
  onSuccess,
  submitCreate = bopsApi.postCreateCredentials,
}: CredentialsFormProps) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormFields>({
    // Prefill non-secret, non-virtual fields only; secret and virtual fields start empty.
    // In update mode, leaving a secret field empty means "keep existing".
    defaultValues:
      mode === 'update' && existingCredential
        ? Object.fromEntries(
            fields
              .filter((field) => !isSecretField(field) && !field.isVirtualField)
              .map((field) => [
                field.storageKey,
                existingCredential[field.storageKey as StandardCredentialFieldKey] ?? '',
              ]),
          )
        : {},
  });

  const isCreate = mode === 'create';
  const titleKey = isCreate ? 'Credentials.Form.ConnectTitle' : 'Credentials.Form.UpdateTitle';
  const submitLabelKey = isCreate ? 'Credentials.Form.Connect' : 'Credentials.Form.Update';
  const descriptionKey = `Credentials.Form.Description.${connectorName}`;
  const description = t(descriptionKey, { name: connectorName, defaultValue: '' });
  const finalDescription = description || t('Credentials.Form.Description.Default', { name: connectorName });

  const onSubmit = async (data: FormFields) => {
    setSubmitting(true);

    const virtualField = fields.find((f) => f.isVirtualField);
    const virtualValue = virtualField ? data[virtualField.storageKey] ?? '' : '';

    const standardEntries = Object.fromEntries(
      fields
        .filter((f) => !f.isVirtualField)
        .map((field) => {
          const key = field.storageKey as StandardCredentialFieldKey;
          const typed = data[key] ?? '';
          const resolved = !isCreate && !typed && isSecretField(field) ? existingCredential?.[key] ?? '' : typed;
          return [key, resolved];
        }),
    );

    const payload = {
      connectorType,
      ...standardEntries,
      ...(virtualValue ? { connectorConfig: { url: virtualValue } } : {}),
    } as ProviderCredential;

    try {
      if (isCreate) {
        await submitCreate(payload);
        showSuccessToast(t('Credentials.CreateSuccess'));
      } else {
        await bopsApi.postUpdateCredentials(payload);
        showSuccessToast(t('Credentials.UpdateSuccess'));
      }
      onSuccess();
    } catch (error: unknown) {
      const err = error as {
        response?: {
          data?: {
            detail?: { error_code?: string; status_reason?: string | null } | string;
            message?: string;
          };
          status?: number;
        };
        message?: string;
      };
      sendErrorEvent(
        `Error occurred while ${isCreate ? 'creating' : 'updating'} credentials. Error: ${
          err.response?.data?.message ?? err.message ?? ''
        }`,
      );
      const detail = err.response?.data?.detail;
      const errorCode = typeof detail === 'object' ? detail?.error_code : undefined;
      const statusReason = typeof detail === 'object' ? detail?.status_reason : undefined;
      let errorMessage = t('Credentials.Error');
      if (errorCode === 'INVALID_CREDENTIALS' && statusReason && STATUS_REASON_CODES[statusReason]) {
        errorMessage = t(STATUS_REASON_CODES[statusReason]);
      } else if (errorCode && ERROR_CODES[errorCode]) {
        errorMessage = t(ERROR_CODES[errorCode]);
      } else if (err.response?.status === 409) {
        errorMessage = t('Credentials.CredentialAlreadyExists');
      }
      showErrorToast(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="relative flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-col gap-1">
        <h4 className="text-sm font-semibold text-gray-900">{t(titleKey, { name: connectorName })}</h4>
        <p className="text-xs leading-relaxed text-gray-500">{finalDescription}</p>
      </div>

      <div className="flex flex-col gap-3">
        {fields.map((field) => (
          <CredentialFieldInput
            key={field.storageKey}
            field={field}
            mode={mode}
            register={register}
            watch={watch}
            error={errors[field.storageKey]}
          />
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-10 flex-1 border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          onClick={onCancel}
          disabled={submitting}>
          {t('Credentials.Form.Cancel')}
        </Button>
        <Button type="submit" className="h-10 flex-1 bg-brand-600 text-white hover:bg-brand-700" disabled={submitting}>
          {t(submitLabelKey)}
        </Button>
      </div>

      {submitting && (
        <div
          aria-busy="true"
          aria-live="polite"
          className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-gray-50/85 backdrop-blur-[2px]">
          <span className="relative flex h-12 w-12 items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-brand-500/20 [animation-duration:1.8s]" />
            <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-[0_1px_2px_rgba(15,23,42,0.08)]">
              <Loader2 className="h-5 w-5 animate-spin text-brand-600" strokeWidth={2.25} />
            </span>
          </span>
        </div>
      )}
    </form>
  );
};

export default CredentialsForm;
